import type { Chef } from '../types/chef';
import { distanceKm } from './geo';
import { fromPricePerGuest } from './pricing';

export type SortKey = 'recommended' | 'price_asc' | 'rating' | 'response';

export interface Filters {
  where: string;
  when: string;
  guests: number;
  fulfillment: 'any' | 'in_home' | 'delivery';
  cuisines: string[];
  maxPricePerGuest: number;
  minRating: number;
  dietary: string[];
  verifiedOnly: boolean;
  instantOnly: boolean;
  maxDistance: number;
}

export const defaultFilters: Filters = {
  where: 'Phrom Phong, Bangkok',
  when: '',
  guests: 6,
  fulfillment: 'any',
  cuisines: [],
  maxPricePerGuest: 6000,
  minRating: 0,
  dietary: [],
  verifiedOnly: false,
  instantOnly: false,
  maxDistance: 30,
};

export interface ChefResult {
  chef: Chef;
  price: number;
  distance: number;
  score: number;
}

export function activeFilterCount(filters: Filters): number {
  let count = 0;
  if (filters.fulfillment !== 'any') count += 1;
  count += filters.cuisines.length;
  if (filters.maxPricePerGuest < defaultFilters.maxPricePerGuest) count += 1;
  if (filters.minRating > 0) count += 1;
  count += filters.dietary.length;
  if (filters.verifiedOnly) count += 1;
  if (filters.instantOnly) count += 1;
  if (filters.maxDistance < defaultFilters.maxDistance) count += 1;
  return count;
}

export function searchChefs(
  chefs: Chef[],
  filters: Filters,
  origin: { lat: number; lng: number },
  sort: SortKey = 'recommended',
): ChefResult[] {
  const results = chefs
    .map((chef) => {
      const price = fromPricePerGuest(chef);
      const distance = distanceKm(origin, { lat: chef.lat, lng: chef.lng });
      const score =
        chef.rating * 12 +
        chef.repeatRate * 0.35 +
        Math.max(0, 120 - chef.responseMinutes) * 0.2 -
        distance * 0.9 -
        price * 0.002;
      return { chef, price, distance, score };
    })
    .filter(({ chef, price, distance }) => {
      if (filters.fulfillment !== 'any' && !chef.fulfillment.includes(filters.fulfillment)) return false;
      if (filters.cuisines.length && !filters.cuisines.some((id) => chef.cuisineIds.includes(id))) return false;
      if (price > filters.maxPricePerGuest) return false;
      if (chef.rating < filters.minRating) return false;
      if (filters.dietary.length && !filters.dietary.every((cap) => chef.dietaryCapabilities.includes(cap))) return false;
      if (filters.verifiedOnly && chef.verifications.some((v) => v.status !== 'verified')) return false;
      if (filters.instantOnly && chef.bookingMode !== 'instant') return false;
      if (distance > filters.maxDistance) return false;
      const menus = chef.menus.filter((m) => (filters.fulfillment === 'any' ? true : m.fulfillment.includes(filters.fulfillment)));
      if (!menus.some((m) => m.minGuests <= filters.guests)) return false;
      return true;
    });

  const sorters: Record<SortKey, (a: ChefResult, b: ChefResult) => number> = {
    recommended: (a, b) => b.score - a.score,
    price_asc: (a, b) => a.price - b.price,
    rating: (a, b) => b.chef.rating - a.chef.rating || b.chef.reviewCount - a.chef.reviewCount,
    response: (a, b) => a.chef.responseMinutes - b.chef.responseMinutes,
  };

  return results.sort(sorters[sort]);
}
